import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Brightness1, WarningAmber } from '@mui/icons-material';
import { Grid } from '@mui/material';

import { TableComponent } from 'commons/components/Table';
import {
  PickingBySKUsDetails,
  PickingListBySKUs,
} from 'commons/types/pickingList.interface';
import { getPickTaskBySKU, validatePickingList } from 'redux-stores/actions';
import { selectUser } from 'redux-stores/features/authSlice';
import { RootReducerInterface } from 'redux-stores/reducers';
import { updateSKUList } from 'redux-stores/reducers/pickingTaskReducer';
import { AppDispatch } from 'redux-stores/store';
import { PICK_TASK_ROUTES } from 'routes/constants';

import { onRequestStocks } from '../commons/components/utils';

import S from './PickingListPage.styles';
import { config } from './pickingListTable.config';

const PickingListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [isProceedButtonDisabled, setProceedButtonDisabled] = useState(false);

  const user = useSelector(selectUser);
  const { tasksBySKUs, skuLowStockTriggered, invalidSKUs } = useSelector(
    (state: RootReducerInterface) => state.pickingTask,
  );

  useEffect(() => {
    const updatePickTaskBySKU = async (): Promise<void> => {
      dispatch(getPickTaskBySKU());
    };

    updatePickTaskBySKU();
  }, [dispatch]);

  const [filter, setFilter] = useState<string>('');
  const [displayedTasks, setDisplayedTasks] =
    useState<PickingListBySKUs[]>(tasksBySKUs);
  const [displayedIndex, setDisplayedIndex] = useState<number[]>([]);

  useEffect(() => {
    setDisplayedTasks(tasksBySKUs);
    setDisplayedIndex(tasksBySKUs.map((_, idx) => idx));
    setProceedButtonDisabled(
      validatePickingList(tasksBySKUs, dispatch).validation === 'error',
    );
  }, [tasksBySKUs, dispatch]);

  useEffect(() => {
    const skuList = tasksBySKUs.map((task) => task.sku_code);
    dispatch(updateSKUList(skuList));
  }, [dispatch, tasksBySKUs]);

  const onFilterChange = (value: string): void => {
    setFilter(value);
    if (value === '') {
      setDisplayedTasks(tasksBySKUs);
      setDisplayedIndex(tasksBySKUs.map((_, idx) => idx));
    } else {
      const filteredIdx: number[] = [];
      const filteredTasks = tasksBySKUs.filter((task, idx) => {
        if (task.sku_code.includes(value)) {
          filteredIdx.push(idx);
          return true;
        }
        return false;
      });
      setDisplayedTasks(filteredTasks);
      setDisplayedIndex(filteredIdx);
    }
  };

  const cleanUpTasksBySKU = (): void => {
    tasksBySKUs.forEach((task) => {
      task.details.forEach((detail) => {
        let emptyPick: number;
        do {
          emptyPick = -1;
          emptyPick = detail.picked_actual.findIndex(
            (actual) => actual.quantity <= 0,
          );
          if (emptyPick >= 0) {
            detail.picked_actual.splice(emptyPick, 1);
          }
        } while (emptyPick >= 0);
      });
    });
  };

  const goToSummaryPage = (): void => {
    const validationStatus = validatePickingList(tasksBySKUs, dispatch);
    if (validationStatus.validation === 'error') {
      return;
    }

    cleanUpTasksBySKU();
    navigate(PICK_TASK_ROUTES.SUMMARY);
  };

  const renderList = (pickingList: PickingListBySKUs[]) =>
    pickingList.map((data, idx) => {
      const pickedProgress = data.details.reduce(
        (sum, detail) =>
          sum +
          detail.picked_actual.reduce(
            (sum2, actual) => sum2 + actual.quantity,
            0,
          ),
        0,
      );
      const colorProgress =
        pickedProgress === 0
          ? S.colors.PROGRESS_ICON_COLOR[0]
          : pickedProgress !== data.total_quantity || invalidSKUs[data.sku_code]
          ? S.colors.PROGRESS_ICON_COLOR[2]
          : S.colors.PROGRESS_ICON_COLOR[1];
      const skuButtonText = pickedProgress === 0 ? 'PICK SKU' : 'EDIT SKU';

      const recommendationTotalQty = data.pick_recommendation.reduce(
        (total, rec) =>
          rec.bin_type === 'retail' ? total + rec.quantity : total,
        0,
      );
      const isStockOut =
        !skuLowStockTriggered[data.sku_code] &&
        pickedProgress < data.total_quantity
          ? recommendationTotalQty < data.total_quantity
          : false;

      const getTableData = (): (PickingBySKUsDetails & { bin?: string })[] => {
        const result: PickingBySKUsDetails[] = data.details.map((detail) => {
          const bin = data.pick_recommendation[0];
          return { ...detail, bin: bin?.bin || '-' };
        });
        return result;
      };

      return (
        <Grid container>
          <S.SKUGridWrapper container>
            <S.SKUGrid item xs={8} data-testid={`TextListSKU-${idx + 1}`}>
              {data.sku_code}
              <S.ProgressSpan data-testid={`TextListQTYPicked-${idx + 1}`}>
                <Brightness1 style={{ fontSize: 11, color: colorProgress }} />{' '}
                {pickedProgress}/{data.total_quantity}
              </S.ProgressSpan>
            </S.SKUGrid>
            <S.SKUButtonGrid item xs={4}>
              <S.SecondaryButton
                onClick={(): void =>
                  navigate(PICK_TASK_ROUTES.ITEM, {
                    state: { pickIdx: displayedIndex[idx] },
                  })
                }
                disabled={isStockOut}
                data-testid={`BtnListPick-${idx + 1}`}
              >
                {invalidSKUs[data.sku_code] && (
                  <WarningAmber
                    style={{ marginRight: '8px' }}
                    fontSize="small"
                  />
                )}
                {skuButtonText}
              </S.SecondaryButton>
            </S.SKUButtonGrid>
          </S.SKUGridWrapper>
          <S.TableInfoGridWrapper container>
            <S.TableInfoGrid item xs={12}>
              <TableComponent config={config} data={getTableData() || []} />
            </S.TableInfoGrid>
            {invalidSKUs[data.sku_code] && (
              <S.TableInfoGrid className="error-message no-pad-top" item xs={8}>
                <WarningAmber fontSize="small" />
                {invalidSKUs[data.sku_code].message}
              </S.TableInfoGrid>
            )}
            {isStockOut && (
              <>
                <S.TableInfoGrid
                  className="error-message no-pad-top"
                  item
                  xs={7}
                >
                  <WarningAmber fontSize="small" />
                  This SKU is out of stock.
                </S.TableInfoGrid>
                <S.TableInfoGrid
                  className="no-pad-top"
                  style={{ alignItems: 'end' }}
                  item
                  xs={5}
                >
                  <S.BlueButtonText
                    style={{ padding: 0, float: 'right' }}
                    onClick={() =>
                      onRequestStocks(data, user.warehouseId, dispatch)
                    }
                  >
                    REQUEST STOCKS
                  </S.BlueButtonText>
                </S.TableInfoGrid>
              </>
            )}
          </S.TableInfoGridWrapper>
        </Grid>
      );
    });

  return (
    <div>
      {/* Content Section */}
      <S.ContentWrapper>
        <S.SubtitleWrapper container>
          <S.Subtitle item xs={12} data-testid="TextListPick">
            Pick List
          </S.Subtitle>
        </S.SubtitleWrapper>
        <S.SearchBar
          placeholder="SKU Number or Scan"
          variant="outlined"
          value={filter}
          onChange={(event) => onFilterChange(event.target.value)}
          inputProps={{
            'data-testid': 'InputListSKU',
          }}
        />

        {renderList(displayedTasks)}
        <S.BlankWhiteSpace />
      </S.ContentWrapper>

      {/* Footer Section */}
      <S.FooterWrapper>
        <S.PrimaryButton
          disabled={isProceedButtonDisabled}
          onClick={goToSummaryPage}
          data-testid="BtnListProceed"
        >
          PROCEED
        </S.PrimaryButton>
      </S.FooterWrapper>
    </div>
  );
};

export default PickingListPage;
